import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import debounce from 'lodash/debounce';
import { useRecoilValue } from 'recoil';

import { ia, iaRa, snakeToTitleCase } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';
import useTimezone from 'lib/hooks/useTimezone';

import NotFound from 'components/practice/Dashboard/components/NotFound';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import ActivitiesLogSkeleton from './ActivitiesLogSkeleton';
import ActivityLog from './components/ActivityLog';
import Comment from './components/Comment';
import CommentInput from './components/CommentInput';
import { exportActivitiesToPdf } from './lib/activityPdf/exportActivitiesToPdf';
import { expandActivityLogByIndex } from './lib/expandActivityLogByIndex';
import { formatChangedValues } from './lib/formatChangedValues';
import { formatDateHeader, groupActivitiesByDate } from './lib/groupActivitiesByDate';
import { useComment } from './useComment';

const ActivitiesLog = forwardRef((props, ref) => {
  const {
    id,
    fetchData,
    category,
    headerConfigs,
    customActivityFormatter = (activities) => activities,
    overwriteActivities = {},
    type,
    filterItems,
    extraParams = {},
    queryOptions = {}
  } = props;

  const scrollRef = useRef(null);
  const shouldScrollToBottom = useRef(true);
  const scrollAdjustmentRef = useRef(null);
  const userHasScrolled = useRef(false);
  const permission = useRecoilValue(state.permissions);
  const timezone = useTimezone();
  const practice = useRecoilValue(currentPractice);

  const [activeFilter, setActiveFilter] = useState('all');

  const handleFilterChange = (item) => {
    shouldScrollToBottom.current = true;
    userHasScrolled.current = false;
    setActiveFilter(item.value);
  };

  const filterType = activeFilter === 'all' ? type : activeFilter;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    resetRefParams
  } = useInfiniteFetchData({
    queryKey: 'activities',
    queryFn: fetchData,
    params: { id, type: filterType, ...extraParams },
    dependencies: [category, id, filterType, JSON.stringify(extraParams)],
    options: { staleTime: Infinity, keepPreviousData: true, refetchOnMount: true, ...queryOptions }
  });

  const { isCreatingComment, onCommentPostHandler, onCommentDeleteHandler, onCommentSaveHandler } =
    useComment(category, id, extraParams);

  const handleCommentPost = async (value) => {
    shouldScrollToBottom.current = true;
    await onCommentPostHandler(value);
  };

  const getFinalActivities = (propData = data) => {
    const expandedActivities = expandActivityLogByIndex(iaRa(propData)).sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    const customActivities = customActivityFormatter(expandedActivities);

    const formattedActivities = formatChangedValues({ activities: customActivities, timezone });

    const groupedActivities = groupActivitiesByDate(formattedActivities, timezone);

    return groupedActivities;
  };

  const finalGroupedActivities = useMemo(
    () => getFinalActivities(),
    [data, timezone, customActivityFormatter]
  );

  const handleExportPdf = useCallback(async () => {
    try {
      const newData = await refetch({ params: { forPdfExport: true } });
      resetRefParams();

      const groupedActivities = getFinalActivities(newData);

      const title = `${snakeToTitleCase(category) || ''} activity log`;

      await exportActivitiesToPdf({
        groupedActivities,
        title,
        category,
        headerConfigs: { ...headerConfigs, practice },
        isViewerAdmin: !!permission?.admin_id,
        timezone
      });
    } catch (error) {
      console.error('Failed to export an activity log to a PDF:', error);
      showAlert({ message: 'Failed to export an activity log to a PDF', color: 'danger' });
    }
  }, [refetch, category, practice, permission?.admin_id, headerConfigs, timezone]);

  useImperativeHandle(ref, () => ({ handleExportPdf }), [handleExportPdf]);

  // Scroll to bottom on initial load and after posting a comment
  useEffect(() => {
    if (scrollRef.current && (isLoading || shouldScrollToBottom.current)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      shouldScrollToBottom.current = false;
    }
  }, [isLoading, data]);

  // Adjust scroll position synchronously before browser paint to prevent visible jump
  useLayoutEffect(() => {
    if (scrollAdjustmentRef.current && scrollRef.current) {
      const { scrollTop, scrollHeight } = scrollAdjustmentRef.current;
      const heightDifference = scrollRef.current.scrollHeight - scrollHeight;
      if (heightDifference > 0) {
        scrollRef.current.scrollTop = scrollTop + heightDifference;
      }
      scrollAdjustmentRef.current = null;
    }
  }, [data]);

  // Mark that user has scrolled after the initial programmatic scroll-to-bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isLoading) return;

    let ignoreNextScroll = true;
    const onScroll = () => {
      if (ignoreNextScroll) {
        ignoreNextScroll = false;
        return;
      }
      userHasScrolled.current = true;
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [isLoading, data]);

  // Fetch next page when user scrolls to the top
  useEffect(() => {
    if (isLoading || !hasNextPage) return;

    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = debounce(() => {
      if (userHasScrolled.current && el.scrollTop < 50 && hasNextPage && !isFetchingNextPage) {
        scrollAdjustmentRef.current = {
          scrollTop: el.scrollTop,
          scrollHeight: el.scrollHeight
        };
        fetchNextPage();
      }
    }, 200);

    el.addEventListener('scroll', handleScroll);
    return () => {
      handleScroll.cancel();
      el.removeEventListener('scroll', handleScroll);
    };
  }, [isLoading, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <ActivitiesLogSkeleton />;

  if (isError) {
    return (
      <NotFound
        className="justify-start pt-16"
        icon="illustration-no-content"
        title="Unable to load activities"
        description="Please try again later."
      />
    );
  }

  return (
    <>
      {ia(filterItems) && (
        <div className="w-full border-0 border-b border-solid border-b-neutral-100 bg-white px-4 py-2">
          <div className="max-w-full">
            <ToggleSwitch items={filterItems} active={activeFilter} onClick={handleFilterChange} />
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        className="relative flex flex-1 flex-col justify-between gap-5 overflow-y-auto px-5 pb-5">
        {isFetchingNextPage && (
          <div className="align-center flex justify-center pb-2">
            <Icon icon="loader-linear" className="animate-spin" color="primary" size={28} />
          </div>
        )}
        <div className="flex flex-col gap-8">
          {!ia(data) ? (
            <NotFound
              className="justify-start pt-16"
              icon="illustration-no-content"
              description="No activities yet"
              descriptionClassName="text-base text-neutral-900"
            />
          ) : (
            finalGroupedActivities.dateKeys.map((dateKey) => {
              const activities = finalGroupedActivities.grouped[dateKey];

              return (
                <div key={dateKey} className="flex flex-col gap-4">
                  <div className="sticky top-0 z-10 bg-white py-1">
                    <Typography size="small" weight="semibold" color="neutral-800">
                      {formatDateHeader(activities[0]?.created_at, timezone)}
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-5">
                    {iaRa(activities).map((activity, activityIndex) => {
                      return (
                        <div key={activity.id || `${dateKey}-${activityIndex}`}>
                          {activity.type === 'comment' ? (
                            <Comment
                              comment={activity}
                              category={category}
                              refId={id}
                              onDelete={onCommentDeleteHandler}
                              onSave={onCommentSaveHandler}
                            />
                          ) : (
                            <ActivityLog
                              overwriteActivities={overwriteActivities}
                              category={category}
                              activity={activity}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="w-full border-0 border-t border-solid border-t-neutral-200 p-3">
        <CommentInput
          onPost={handleCommentPost}
          loading={isCreatingComment}
          placeholder="Add comment"
        />
      </div>
    </>
  );
});

ActivitiesLog.displayName = 'ActivitiesLog';

export default ActivitiesLog;
